import React, { useEffect, useState, useCallback } from "react";
import { PageShell } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  CurrencyInr, TrendUp, TrendDown, Calendar, ChartBar, Fire, Sparkle,
  Wallet, Scroll, Toolbox, Crown,
} from "@phosphor-icons/react";

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------
const fmtINR = (n) => "₹" + Math.round(Number(n || 0)).toLocaleString("en-IN");
const fmtNum = (n) => Number(n || 0).toLocaleString("en-IN");

const STREAM_META = {
  wallet:      { label: "Wallet top-ups", color: "#F59E0B", icon: Wallet },
  twenty_year: { label: "20-Year Forecast", color: "#059669", icon: Scroll },
  tool:        { label: "Premium tools", color: "#7C3AED", icon: Toolbox },
  vip:         { label: "VIP King Pack", color: "#DC2626", icon: Crown },
};
const TOOL_LABELS = {
  bhrigu: "Bhrigu Bindu",
  chakras: "Chakra Heat-Map",
  nakshatra: "Nakshatra Analysis",
  compatibility: "Compatibility",
  rahu: "Rahu Placement",
  planet_effects: "9-Planet Effects",
  navamsa: "D9 Navamsa",
  numerology_deep: "Deep Numerology",
  twenty_year: "20-Year Forecast",
};

// -----------------------------------------------------------------------------
// Delta pill — green up, red down, neutral grey
// -----------------------------------------------------------------------------
const DeltaPill = ({ pct }) => {
  if (pct === null || pct === undefined) {
    return <span className="text-[10px] text-stone-400">—</span>;
  }
  const up = pct >= 0;
  const Icon = up ? TrendUp : TrendDown;
  const cls = up
    ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : "text-rose-700 bg-rose-50 border-rose-200";
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${cls}`}>
      <Icon size={10} weight="bold" />
      {up ? "+" : ""}{pct}%
    </span>
  );
};

// -----------------------------------------------------------------------------
// KPI tile
// -----------------------------------------------------------------------------
const Kpi = ({ label, kpi, sub, testid }) => (
  <div className="rounded-2xl p-5 bg-white/80 backdrop-blur border border-amber-200 shadow-sm"
       data-testid={testid}>
    <div className="flex items-center justify-between gap-2">
      <div className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#7C2D12]">{label}</div>
      <DeltaPill pct={kpi?.delta_pct} />
    </div>
    <div className="font-serif-display text-3xl mt-3 font-light text-[#3F2D1B]"
         data-testid={`${testid}-value`}>
      {fmtINR(kpi?.revenue_inr)}
    </div>
    <div className="text-xs text-stone-500 mt-1">
      {fmtNum(kpi?.orders)} order{(kpi?.orders || 0) === 1 ? "" : "s"}
      {sub && <span className="ml-1 text-stone-400">· {sub}</span>}
    </div>
  </div>
);

// -----------------------------------------------------------------------------
// Vertical bar chart for the 30-day daily series
// -----------------------------------------------------------------------------
const DailyChart = ({ daily }) => {
  const max = Math.max(1, ...daily.map((d) => d.revenue_inr));
  return (
    <div className="rounded-2xl bg-white/80 border border-amber-200 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-bold text-[#3F2D1B]">
          Daily revenue · last {daily.length} days
        </div>
        <div className="text-[10px] text-stone-500">
          Peak: <span className="font-semibold text-emerald-700">{fmtINR(max)}</span>
        </div>
      </div>
      <div className="flex items-end gap-1 h-44 overflow-x-auto pb-6 relative">
        {daily.map((d, i) => {
          const h = Math.max(2, (d.revenue_inr / max) * 100);
          const isWeekend = ["Sat", "Sun"].includes(
            new Date(d.date).toLocaleDateString("en-US", { weekday: "short" })
          );
          return (
            <div key={d.date}
                 className="flex flex-col items-center justify-end flex-1 min-w-[8px] h-full relative"
                 title={`${d.date} · ${fmtINR(d.revenue_inr)} · ${d.orders} order${d.orders === 1 ? "" : "s"}`}>
              {/* The bar fills `h%` of the column height. Inside it we stack
                  the four stream segments proportionally so the same bar
                  shows the breakdown of revenue per stream for that day. */}
              <div className="w-full flex flex-col rounded-t overflow-hidden"
                   style={{ height: `${h}%` }}>
                {(() => {
                  const total = ["twenty_year", "wallet", "tool", "vip"]
                    .reduce((s, k) => s + (d[k] || 0), 0);
                  if (total <= 0) {
                    return <div className="w-full h-full bg-stone-200" />;
                  }
                  return ["twenty_year", "wallet", "tool", "vip"].map((s) => {
                    const v = d[s] || 0;
                    if (v <= 0) return null;
                    return (
                      <div key={s}
                           style={{
                             height: `${(v / total) * 100}%`,
                             backgroundColor: STREAM_META[s].color,
                           }}
                           className="w-full" />
                    );
                  });
                })()}
              </div>
              {(i % Math.max(1, Math.floor(daily.length / 8)) === 0) && (
                <div className={`text-[8px] mt-1 absolute -bottom-5 left-1/2 -translate-x-1/2 rotate-[-30deg] origin-top whitespace-nowrap ${isWeekend ? "text-rose-600 font-semibold" : "text-stone-500"}`}>
                  {d.date.slice(5)}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex gap-3 mt-4 flex-wrap text-[11px]">
        {Object.entries(STREAM_META).map(([k, m]) => (
          <div key={k} className="flex items-center gap-1.5">
            <span style={{ backgroundColor: m.color }} className="w-3 h-3 rounded" />
            <span className="text-stone-600">{m.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Monthly comparison (last 6 months)
// -----------------------------------------------------------------------------
const MonthlyChart = ({ monthly }) => {
  const max = Math.max(1, ...monthly.map((m) => m.revenue_inr));
  return (
    <div className="rounded-2xl bg-white/80 border border-amber-200 p-5 shadow-sm">
      <div className="text-sm font-bold text-[#3F2D1B] mb-3">
        Month-over-month · last 6 months
      </div>
      <div className="space-y-2.5">
        {monthly.map((m, i) => (
          <div key={m.month} className="flex items-center gap-3" data-testid={`mom-row-${m.month}`}>
            <div className="text-xs font-bold w-20 text-stone-600">{m.label}</div>
            <div className="flex-1 relative bg-stone-100 rounded-full h-7 overflow-hidden">
              <div
                style={{
                  width: `${(m.revenue_inr / max) * 100}%`,
                  background: `linear-gradient(90deg, #B45309, #F59E0B)`,
                }}
                className="h-full rounded-full transition-all"
              />
              <div className="absolute inset-0 flex items-center px-3 text-[11px] text-[#3F2D1B] font-bold">
                {fmtINR(m.revenue_inr)}
                <span className="ml-2 text-stone-500 font-normal">· {fmtNum(m.orders)} ord.</span>
              </div>
            </div>
            <div className="w-16 text-right">
              {i > 0 && <DeltaPill pct={m.delta_pct} />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// By-stream donut (CSS only)
// -----------------------------------------------------------------------------
const StreamSplit = ({ by_stream }) => {
  const total = by_stream.reduce((s, x) => s + x.revenue_inr, 0) || 1;
  let acc = 0;
  const segments = by_stream.map((s) => {
    const start = (acc / total) * 360;
    acc += s.revenue_inr;
    const end = (acc / total) * 360;
    return { ...s, start, end, pct: (s.revenue_inr / total) * 100 };
  });
  const grad = segments.map((s) =>
    `${STREAM_META[s.stream]?.color || "#999"} ${s.start}deg ${s.end}deg`
  ).join(", ");
  return (
    <div className="rounded-2xl bg-white/80 border border-amber-200 p-5 shadow-sm">
      <div className="text-sm font-bold text-[#3F2D1B] mb-3">Revenue by stream · all-time</div>
      <div className="flex items-center gap-5">
        <div
          className="w-32 h-32 rounded-full shrink-0 relative"
          style={{ background: `conic-gradient(${grad || "#E5E7EB 0deg 360deg"})` }}
        >
          <div className="absolute inset-3 bg-white rounded-full flex flex-col items-center justify-center text-center">
            <div className="text-[10px] text-stone-500">Total</div>
            <div className="text-sm font-bold text-[#3F2D1B]">{fmtINR(total)}</div>
          </div>
        </div>
        <div className="flex-1 space-y-2">
          {segments.length === 0 && (
            <div className="text-xs text-stone-400 italic">No revenue yet.</div>
          )}
          {segments.map((s) => {
            const meta = STREAM_META[s.stream] || { label: s.stream, color: "#999" };
            return (
              <div key={s.stream} className="flex items-center gap-2 text-xs">
                <span style={{ backgroundColor: meta.color }} className="w-3 h-3 rounded shrink-0" />
                <span className="flex-1 text-stone-700">{meta.label}</span>
                <span className="font-bold text-[#3F2D1B]">{fmtINR(s.revenue_inr)}</span>
                <span className="text-stone-500 w-10 text-right">{s.pct.toFixed(0)}%</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Tools table
// -----------------------------------------------------------------------------
const ToolsTable = ({ by_tool }) => (
  <div className="rounded-2xl bg-white/80 border border-amber-200 p-5 shadow-sm">
    <div className="text-sm font-bold text-[#3F2D1B] mb-3">Premium-tool revenue</div>
    {by_tool.length === 0 ? (
      <div className="text-xs text-stone-400 italic">No tool revenue yet.</div>
    ) : (
      <table className="w-full text-xs">
        <thead className="text-[10px] text-stone-500 uppercase tracking-wider">
          <tr><th className="text-left pb-2">Tool</th><th className="text-right pb-2">Orders</th><th className="text-right pb-2">Revenue</th></tr>
        </thead>
        <tbody>
          {by_tool.map((t) => (
            <tr key={t.tool} className="border-t border-amber-100">
              <td className="py-2 text-stone-800 font-medium">
                {TOOL_LABELS[t.tool] || t.tool}
              </td>
              <td className="py-2 text-right text-stone-600">{fmtNum(t.orders)}</td>
              <td className="py-2 text-right font-bold text-[#3F2D1B]">{fmtINR(t.revenue_inr)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

// -----------------------------------------------------------------------------
// Insights list
// -----------------------------------------------------------------------------
const InsightsList = ({ insights }) => (
  <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-300 p-5 shadow-sm">
    <div className="text-sm font-bold text-[#3F2D1B] mb-3 flex items-center gap-2">
      <Sparkle size={16} weight="duotone" className="text-amber-700" /> Smart insights
    </div>
    {insights.length === 0 ? (
      <div className="text-xs text-stone-500 italic">No insights yet — start receiving payments.</div>
    ) : (
      <div className="space-y-3">
        {insights.map((it, i) => (
          <div key={i} className="bg-white/70 rounded-xl p-3 border border-amber-200">
            <div className="text-sm font-bold text-[#3F2D1B] flex items-center gap-2">
              <span>{it.emoji}</span> {it.title}
            </div>
            <div className="text-xs text-stone-600 mt-1">{it.body}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// -----------------------------------------------------------------------------
// Top days
// -----------------------------------------------------------------------------
const TopDays = ({ top_days }) => (
  <div className="rounded-2xl bg-white/80 border border-amber-200 p-5 shadow-sm">
    <div className="text-sm font-bold text-[#3F2D1B] mb-3 flex items-center gap-2">
      <Fire size={16} weight="duotone" className="text-rose-600" /> Top 5 revenue days · last 30
    </div>
    {top_days.length === 0 || top_days[0].revenue_inr === 0 ? (
      <div className="text-xs text-stone-400 italic">No revenue in this window.</div>
    ) : (
      <div className="space-y-2">
        {top_days.map((d, i) => (
          <div key={d.date} className="flex items-center justify-between text-xs py-1.5 border-b border-amber-50 last:border-0">
            <div className="flex items-center gap-2">
              <span className="text-stone-400 font-bold w-4">{i + 1}.</span>
              <span className="text-stone-800 font-medium">{d.date}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-stone-500">{fmtNum(d.orders)} ord.</span>
              <span className="font-bold text-[#3F2D1B]">{fmtINR(d.revenue_inr)}</span>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// -----------------------------------------------------------------------------
// Top products card (NEW)
// -----------------------------------------------------------------------------
const TopProducts = ({ top_products }) => {
  const max = Math.max(1, ...top_products.map((p) => p.revenue_inr));
  return (
    <div className="rounded-2xl bg-white/80 border border-amber-200 p-5 shadow-sm">
      <div className="text-sm font-bold text-[#3F2D1B] mb-3 flex items-center gap-2">
        <Crown size={16} weight="duotone" className="text-amber-700" /> Top 5 products by revenue
      </div>
      {top_products.length === 0 ? (
        <div className="text-xs text-stone-400 italic">No revenue yet.</div>
      ) : (
        <div className="space-y-2.5">
          {top_products.map((p, i) => (
            <div key={p.product} data-testid={`tp-row-${p.product}`}>
              <div className="flex items-center justify-between text-xs mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-stone-400 font-bold w-4">{i + 1}.</span>
                  <span className="text-stone-800 font-medium">{p.label}</span>
                </div>
                <span className="text-stone-500">{fmtNum(p.orders)} ord.</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 relative bg-stone-100 rounded-full h-6 overflow-hidden">
                  <div style={{
                    width: `${(p.revenue_inr / max) * 100}%`,
                    background: "linear-gradient(90deg, #B45309, #F59E0B)",
                  }} className="h-full" />
                  <div className="absolute inset-0 flex items-center px-3 text-[11px] font-bold text-[#3F2D1B]">
                    {fmtINR(p.revenue_inr)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Top customers table (NEW)
// -----------------------------------------------------------------------------
const TopCustomers = ({ top_customers }) => (
  <div className="rounded-2xl bg-white/80 border border-amber-200 p-5 shadow-sm">
    <div className="text-sm font-bold text-[#3F2D1B] mb-3 flex items-center gap-2">
      <Sparkle size={16} weight="duotone" className="text-emerald-700" /> Top 10 customers by lifetime revenue
    </div>
    {top_customers.length === 0 ? (
      <div className="text-xs text-stone-400 italic">No customers with revenue yet.</div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-[10px] text-stone-500 uppercase tracking-wider">
            <tr>
              <th className="text-left pb-2">#</th>
              <th className="text-left pb-2">Customer</th>
              <th className="text-right pb-2">Orders</th>
              <th className="text-right pb-2">Total revenue</th>
            </tr>
          </thead>
          <tbody>
            {top_customers.map((c, i) => (
              <tr key={c.user_id} className="border-t border-amber-100"
                  data-testid={`tc-row-${i}`}>
                <td className="py-2 text-stone-400 font-bold">{i + 1}</td>
                <td className="py-2">
                  <div className="text-stone-800 font-medium leading-tight">{c.name}</div>
                  <div className="text-[10px] text-stone-500 leading-tight">{c.email}</div>
                </td>
                <td className="py-2 text-right text-stone-600">{fmtNum(c.orders)}</td>
                <td className="py-2 text-right font-bold text-[#3F2D1B]">{fmtINR(c.revenue_inr)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// -----------------------------------------------------------------------------
// Quick-range presets — common date windows admins want one-click access to.
// Returns native `Date` objects in **local** time so the YYYY-MM-DD that ends
// up in the <input type="date"> matches what the user sees on a wall calendar.
// -----------------------------------------------------------------------------
const toISODate = (d) => {
  // Strip the tz offset so toISOString() returns the LOCAL YYYY-MM-DD instead
  // of jumping back a day for users east of UTC after midnight.
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
};

const computePresetRange = (key) => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth(); // 0..11
  switch (key) {
    case "thisMonth":
      return { start: new Date(y, m, 1), end: now };
    case "lastMonth":
      // Day-0 of the current month = last day of the previous month.
      return { start: new Date(y, m - 1, 1), end: new Date(y, m, 0) };
    case "thisQuarter": {
      const q = Math.floor(m / 3); // 0..3
      return { start: new Date(y, q * 3, 1), end: now };
    }
    case "lastQuarter": {
      const q = Math.floor(m / 3);
      const lqYear = q === 0 ? y - 1 : y;
      const lqStartMonth = q === 0 ? 9 : (q - 1) * 3;
      const lqEndMonth = q === 0 ? 11 : q * 3 - 1;
      return {
        start: new Date(lqYear, lqStartMonth, 1),
        end: new Date(lqYear, lqEndMonth + 1, 0),
      };
    }
    case "ytd":
      return { start: new Date(y, 0, 1), end: now };
    default:
      return null;
  }
};

const PRESETS = [
  { key: "thisMonth",   label: "This month" },
  { key: "lastMonth",   label: "Last month" },
  { key: "thisQuarter", label: "This quarter" },
  { key: "lastQuarter", label: "Last quarter" },
  { key: "ytd",         label: "YTD" },
];

// =============================================================================
// MAIN PAGE
// =============================================================================
export default function AdminRevenuePage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  // Date-range mode: when BOTH dates are filled, the API gets called with
  // start_date + end_date and the `days` dropdown is ignored. Empty values
  // fall back to the rolling-window mode.
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const customRange = !!(startDate && endDate);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/"); return; }
    if (!user.is_super_admin) { navigate("/"); return; }
  }, [user, authLoading, navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = customRange
        ? `?start_date=${startDate}&end_date=${endDate}`
        : `?days=${days}`;
      const { data: r } = await api.get(`/admin/revenue${qs}`);
      setData(r);
    } catch (e) {
      console.error("Failed to fetch revenue analytics:", e);
    } finally {
      setLoading(false);
    }
  }, [days, startDate, endDate, customRange]);

  useEffect(() => { if (user?.is_super_admin) fetchData(); }, [fetchData, user]);

  // One-click date-window presets. Setting BOTH dates flips the page into
  // `customRange` mode and the `fetchData` useEffect refreshes automatically.
  const applyPreset = useCallback((key) => {
    const r = computePresetRange(key);
    if (!r) return;
    setStartDate(toISODate(r.start));
    setEndDate(toISODate(r.end));
  }, []);

  // Which preset (if any) is currently reflected by the date inputs — used
  // to highlight the active chip so the admin always knows what they're on.
  const activePreset = (() => {
    if (!customRange) return null;
    for (const p of PRESETS) {
      const r = computePresetRange(p.key);
      if (r && toISODate(r.start) === startDate && toISODate(r.end) === endDate) {
        return p.key;
      }
    }
    return null;
  })();

  const isLoading = authLoading || loading || !data;

  return (
    <PageShell>
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-stone-50 to-orange-50 -my-8 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-7">
          {/* Header */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#7C2D12] font-bold">Super-admin</div>
              <h1 className="font-serif-display text-4xl text-[#3F2D1B] mt-1">
                Revenue Analytics
              </h1>
              <p className="text-sm text-stone-600 mt-1">
                Money in across every paying flow · auto-refreshed from the database.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Rolling-window dropdown — disabled when a custom date range is active */}
              <select
                value={days}
                onChange={(e) => setDays(parseInt(e.target.value, 10))}
                disabled={customRange}
                data-testid="rev-window-select"
                className="px-3 py-2 rounded-lg border border-amber-300 bg-white text-sm text-[#3F2D1B] focus:outline-none focus:border-[#3F2D1B] disabled:opacity-50 disabled:cursor-not-allowed"
                title={customRange ? "Clear the date range to use a rolling window" : "Rolling window"}
              >
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
                <option value={60}>Last 60 days</option>
                <option value={90}>Last 90 days</option>
                <option value={180}>Last 180 days</option>
              </select>
              {/* Custom date range — when both are filled it OVERRIDES `days` */}
              <div className="flex items-center gap-1 rounded-lg border border-amber-300 bg-white px-2 py-1">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="rev-start-date"
                  className="px-1 py-1 text-[12px] text-[#3F2D1B] focus:outline-none bg-transparent"
                  title="Start date (inclusive)"
                />
                <span className="text-stone-400 text-xs">→</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="rev-end-date"
                  className="px-1 py-1 text-[12px] text-[#3F2D1B] focus:outline-none bg-transparent"
                  title="End date (inclusive)"
                />
                {(startDate || endDate) && (
                  <button
                    onClick={() => { setStartDate(""); setEndDate(""); }}
                    data-testid="rev-clear-range"
                    className="text-stone-400 hover:text-rose-600 text-xs px-1 leading-none"
                    title="Clear date range"
                  >✕</button>
                )}
              </div>
              <button
                onClick={async () => {
                  // CSV export — pass the same window parameters the UI is
                  // currently showing so the file matches the on-screen view
                  // byte-for-byte.
                  try {
                    const qs = customRange
                      ? `?start_date=${startDate}&end_date=${endDate}`
                      : `?days=${days}`;
                    const res = await api.get(
                      `/admin/revenue/export.csv${qs}`,
                      { responseType: "blob", timeout: 60000 }
                    );
                    const blob = new Blob([res.data], { type: "text/csv" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = customRange
                      ? `astrokuber_revenue_${startDate}_to_${endDate}.csv`
                      : `astrokuber_revenue_${new Date().toISOString().slice(0, 10)}_${days}d.csv`;
                    a.rel = "noopener";
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    setTimeout(() => URL.revokeObjectURL(url), 5000);
                  } catch (err) {
                    console.error("CSV export failed:", err);
                  }
                }}
                data-testid="rev-csv-btn"
                className="px-3 py-2 rounded-lg bg-emerald-700 text-white text-xs font-bold tracking-wider hover:bg-emerald-800 inline-flex items-center gap-1.5"
                title="Download every transaction in the selected window as CSV"
              >
                ⬇ EXPORT CSV
              </button>
              <button
                onClick={fetchData}
                disabled={loading}
                data-testid="rev-refresh-btn"
                className="px-3 py-2 rounded-lg bg-[#3F2D1B] text-white text-xs font-bold tracking-wider hover:bg-[#2B1F11] disabled:opacity-60"
              >
                {loading ? "…" : "REFRESH"}
              </button>
            </div>
          </div>

          {/* Quick-range preset chips — let the admin filter to the most
              common windows with a single click instead of typing dates. */}
          <div
            className="flex items-center gap-1.5 flex-wrap -mt-3"
            data-testid="rev-preset-row"
          >
            <span className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-bold mr-1">
              Quick range
            </span>
            {PRESETS.map((p) => {
              const active = activePreset === p.key;
              return (
                <button
                  key={p.key}
                  onClick={() => applyPreset(p.key)}
                  data-testid={`rev-preset-${p.key}`}
                  className={
                    "px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide transition border " +
                    (active
                      ? "bg-[#3F2D1B] text-white border-[#3F2D1B] shadow-sm"
                      : "bg-white text-[#3F2D1B] border-amber-300 hover:bg-amber-100 hover:border-amber-400")
                  }
                >
                  {p.label}
                </button>
              );
            })}
            {customRange && (
              <button
                onClick={() => { setStartDate(""); setEndDate(""); }}
                data-testid="rev-preset-clear"
                className="px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide border border-stone-200 bg-stone-50 text-stone-500 hover:text-rose-600 hover:border-rose-200 transition"
                title="Clear date range and return to rolling window"
              >
                Clear ✕
              </button>
            )}
          </div>

          {/* Active-window indicator — gives the admin a clear at-a-glance of
              exactly what window the dashboard is showing. */}
          {data?.window && (
            <div className="text-[11px] text-stone-500 -mt-3">
              Showing <span className="font-bold text-[#3F2D1B]">{data.window.start}</span>
              {" → "}
              <span className="font-bold text-[#3F2D1B]">{data.window.end}</span>
              <span className="ml-1 text-stone-400">
                ({data.window.days} day{data.window.days === 1 ? "" : "s"}
                {data.window.custom ? " · custom range" : " · rolling window"})
              </span>
            </div>
          )}

          {isLoading ? (
            <div className="rounded-2xl bg-white/60 border border-amber-200 p-12 text-center text-stone-500">
              Computing revenue across every order…
            </div>
          ) : (
            <>
              {/* KPI tiles */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <Kpi label="Today"      kpi={data.kpis.today}      sub="vs yesterday" testid="kpi-today" />
                <Kpi label="Yesterday"  kpi={data.kpis.yesterday}                       testid="kpi-yest" />
                <Kpi label="This week"  kpi={data.kpis.this_week}  sub="vs last week"  testid="kpi-week" />
                <Kpi label="Last week"  kpi={data.kpis.last_week}                       testid="kpi-lweek" />
                <Kpi label="This month" kpi={data.kpis.this_month} sub="vs last month" testid="kpi-month" />
                <Kpi label="YTD"        kpi={data.kpis.ytd}                             testid="kpi-ytd" />
              </div>

              {/* Daily revenue */}
              <DailyChart daily={data.daily} />

              {/* MoM + Stream split */}
              <div className="grid lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2"><MonthlyChart monthly={data.monthly} /></div>
                <StreamSplit by_stream={data.by_stream} />
              </div>

              {/* Insights + Top days + Tools */}
              <div className="grid lg:grid-cols-3 gap-4">
                <InsightsList insights={data.insights} />
                <TopDays top_days={data.top_days} />
                <ToolsTable by_tool={data.by_tool} />
              </div>

              {/* Top products + Top customers */}
              <div className="grid lg:grid-cols-2 gap-4">
                <TopProducts top_products={data.top_products || []} />
                <TopCustomers top_customers={data.top_customers || []} />
              </div>
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
}
